import { useCallback, useEffect, useMemo } from "react"

import { Flex, Typography } from "antd"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { connect, useSelector } from "react-redux"

import ButtonComponent from "../../Components/Button"
import Table from "../../Components/Table"
import Icon from "../../Icon"
import ActivityLogModal from "./components/ActivityLogModal/ActivityLogModal"
import ConfirmDialog from "./components/ConfirmDialog/ConfirmDialog"
import FiltersBar from "./components/FiltersBar/FiltersBar"
import IconConflictDialog from "./components/IconConflictDialog/IconConflictDialog"
import IconPopover from "./components/IconPopover/IconPopover"
import SummaryBlocks from "./components/SummaryBlocks/SummaryBlocks"
import { getColumns } from "./components/Table/columns"
import { usePaymentMatchingEditing } from "./hooks"
import Creators, {
  DEFAULT_ORDERING,
  NO_VALUE_OPTION,
  PAGE_SIZE,
  selectHasActiveFilters,
  SUMMARY_BLOCKS
} from "./reducer"

import styles from "./styles.module.less"

const { Title, Text } = Typography

// Translates the multi-select's own sentinels into query parameters. "all" means the filter imposes
// no restriction, so it contributes nothing; "no_value" is the dropdown's empty option and has to
// become the sentinel the API advertises for "no UI name".
const selectedValues = (value, { noValueAs } = {}) => {
  if (value === false) return noValueAs ? [noValueAs] : undefined

  if (!Array.isArray(value) || !value.length) return undefined

  return value.map((item) => (item === NO_VALUE_OPTION && noValueAs ? noValueAs : item))
}

const PaymentMatchingPage = ({
  fetching,
  result,
  summary,
  filterOptions,
  noUiNameValue,
  iconLibrary,
  iconLibraryFetching,
  saveFetching,
  block,
  filters,
  ordering,
  page,
  getPaymentMatchingRequest,
  getPaymentMatchingSummaryRequest,
  getPaymentMatchingFilterOptionsRequest,
  paymentMatchingSetProp,
  clearPaymentMatchingFilters
}) => {
  const { t } = useTranslation()
  const hasActiveFilters = useSelector(selectHasActiveFilters)

  const { count, results } = result

  const params = useMemo(() => {
    const query = { page, ordering }

    // All values removes the restriction; the other two blocks map onto one boolean.
    if (block === SUMMARY_BLOCKS.MAPPED) query.mapped = true
    if (block === SUMMARY_BLOCKS.UNMAPPED) query.mapped = false

    query.current_name = selectedValues(filters?.current_name)
    query.ui_name = selectedValues(filters?.ui_name, { noValueAs: noUiNameValue })
    query.payment_processor = selectedValues(filters?.payment_processor)

    if (filters?.no_icon) query.no_icon = true

    return query
  }, [page, ordering, block, filters, noUiNameValue])

  useEffect(() => {
    getPaymentMatchingFilterOptionsRequest()
  }, [getPaymentMatchingFilterOptionsRequest])

  useEffect(() => {
    getPaymentMatchingRequest(params)
    // The summary blocks are dataset-wide, but they still have to follow an edit that changes how
    // many values are mapped, so they refresh alongside the table rather than only on mount.
    getPaymentMatchingSummaryRequest()
  }, [params, getPaymentMatchingRequest, getPaymentMatchingSummaryRequest])

  const handleChangeFilter = useCallback(
    (value) => paymentMatchingSetProp("filters", value),
    [paymentMatchingSetProp]
  )

  const handleSelectBlock = useCallback(
    (nextBlock) => paymentMatchingSetProp("block", nextBlock),
    [paymentMatchingSetProp]
  )

  const handleTableChange = useCallback(
    (pagination, _tableFilters, sorter) => {
      paymentMatchingSetProp("page", pagination?.current || 1)

      // Only one column is sorted at a time, and dropping the sort returns to volume descending —
      // the default that puts the values worth mapping at the top.
      if (sorter?.columnKey && sorter?.order) {
        const direction = sorter.order === "ascend" ? "" : "-"
        paymentMatchingSetProp("ordering", `${direction}${sorter.columnKey}`)
      } else {
        paymentMatchingSetProp("ordering", DEFAULT_ORDERING)
      }
    },
    [paymentMatchingSetProp]
  )

  // An edit can make a row stop matching the active filters — naming a row while Unmapped is
  // selected, or attaching an icon while No icon is on. Refetching makes it leave the list at once,
  // which is the intended working pattern rather than something to soften.
  const refetch = useCallback(() => {
    getPaymentMatchingRequest(params)
    getPaymentMatchingSummaryRequest()
    getPaymentMatchingFilterOptionsRequest()
  }, [
    params,
    getPaymentMatchingRequest,
    getPaymentMatchingSummaryRequest,
    getPaymentMatchingFilterOptionsRequest
  ])

  const {
    editing,
    confirm,
    pendingName,
    onConfirm,
    onCancelConfirm,
    conflict,
    conflictChoice,
    setConflictChoice,
    onSaveConflict,
    onCancelConflict,
    iconPopover,
    uploadError,
    onPickLibraryIcon,
    onUploadIcon,
    closeIconPopover,
    activityLog,
    openPageLog,
    closeActivityLog
  } = usePaymentMatchingEditing({ onChanged: refetch })

  const columns = useMemo(() => getColumns({ t, editing, ordering }), [t, editing, ordering])

  const refreshedAt = summary?.counts_refreshed_at
  const isEmpty = !fetching && !results?.length

  return (
    <div className={styles.page} data-testid="payment-matching-page">
      <Flex justify="space-between" align="flex-start" className={styles.header}>
        <div className={styles.headerText}>
          <Title level={3} className={styles.title}>
            {t("Payment Matching")}
          </Title>

          {/* Counts are pre-aggregated, so support has to be able to see how stale the number is
              before acting on it. */}
          <Text type="secondary" className={styles.freshness}>
            {t("Transaction counts are all-time across all business accounts.")}{" "}
            {/* Composed rather than interpolated through i18next: a key carrying a _placeholder_
                renders that placeholder verbatim when no locale defines it, so a missing entry would
                show the user "_date_" instead of simply falling back to English. */}
            {refreshedAt
              ? `${t("Last recalculated")} ${dayjs(refreshedAt).format("DD MMM, YYYY HH:mm")}`
              : t("Not recalculated yet")}
          </Text>
        </div>

        <div className={styles.headerAction}>
          <ButtonComponent icon={<Icon type="bullet_list" />} onClick={openPageLog}>
            {t("Activity log")}
          </ButtonComponent>
        </div>
      </Flex>

      <SummaryBlocks summary={summary} selected={block} onSelect={handleSelectBlock} />

      <FiltersBar
        filters={filters}
        filterOptions={filterOptions}
        onChangeFilter={handleChangeFilter}
      />

      {isEmpty ? (
        <Flex vertical align="center" gap={12} className={styles.empty}>
          <Text type="secondary">{t("No values match the current filters.")}</Text>
          {hasActiveFilters && (
            <ButtonComponent type="primary" onClick={clearPaymentMatchingFilters}>
              {t("Clear filters")}
            </ButtonComponent>
          )}
        </Flex>
      ) : (
        <Table
          rowKey="id"
          loading={fetching}
          dataSource={results}
          columns={columns}
          onChange={handleTableChange}
          // The shared table already renders the "Showing X-Y of Z results" line and the
          // Previous / Next controls, and the page size is fixed on purpose — there is no
          // rows-per-page control.
          showSizeChanger={false}
          pagination={{ total: count, current: page, pageSize: PAGE_SIZE }}
          scroll={{ x: "max-content" }}
        />
      )}

      <ConfirmDialog
        open={confirm.open}
        kind={confirm.kind}
        record={confirm.record}
        nameBefore={confirm.record?.ui_name}
        nameAfter={pendingName}
        affectedRawValues={confirm.affectedRawValues}
        busy={saveFetching}
        onConfirm={onConfirm}
        onCancel={onCancelConfirm}
      />

      <IconConflictDialog
        open={!!conflict}
        record={conflict?.record}
        conflict={conflict}
        selected={conflictChoice}
        busy={saveFetching}
        onSelect={setConflictChoice}
        onSave={onSaveConflict}
        onCancel={onCancelConflict}
      />

      <ActivityLogModal
        open={!!activityLog}
        record={activityLog?.record}
        onClose={closeActivityLog}
      />

      <IconPopover
        open={!!iconPopover}
        record={iconPopover}
        library={iconLibrary}
        libraryFetching={iconLibraryFetching}
        uploadError={uploadError}
        onUpload={onUploadIcon}
        onPick={onPickLibraryIcon}
        onClose={closeIconPopover}
      />
    </div>
  )
}

const mapStateToProps = (state) => ({
  fetching: state.paymentMatching.fetching,
  result: state.paymentMatching.result,
  summary: state.paymentMatching.summary,
  filterOptions: state.paymentMatching.filterOptions,
  noUiNameValue: state.paymentMatching.noUiNameValue,
  iconLibrary: state.paymentMatching.iconLibrary,
  iconLibraryFetching: state.paymentMatching.iconLibraryFetching,
  saveFetching: state.paymentMatching.saveFetching,
  block: state.paymentMatching.block,
  filters: state.paymentMatching.filters,
  ordering: state.paymentMatching.ordering,
  page: state.paymentMatching.page
})

const mapDispatchToProps = (dispatch) => ({
  getPaymentMatchingRequest: (params) => dispatch(Creators.getPaymentMatchingRequest(params)),
  getPaymentMatchingSummaryRequest: () => dispatch(Creators.getPaymentMatchingSummaryRequest()),
  getPaymentMatchingFilterOptionsRequest: () =>
    dispatch(Creators.getPaymentMatchingFilterOptionsRequest()),
  paymentMatchingSetProp: (key, value) => dispatch(Creators.paymentMatchingSetProp(key, value)),
  clearPaymentMatchingFilters: () => dispatch(Creators.clearPaymentMatchingFilters())
})

export default connect(mapStateToProps, mapDispatchToProps)(PaymentMatchingPage)
